<template>
  <div id="batch-create" class="group-setup">
    <v-progress-linear
      indeterminate
      color="blue"
      v-if="loading"
    ></v-progress-linear>

    <v-layout row v-if="errors">
      <v-flex>
        <Alert @dismissed="onDismissed" :text="errors"></Alert>
      </v-flex>
    </v-layout>

    <v-form ref="BatchForm" v-model="valid" v-if="!loading">
      <v-row>
        <v-col cols="12" md="12" sm="12">
          <v-card class="pa-5" flat>
            <HeaderDividerBlock headerText="Create Batch"></HeaderDividerBlock>
            <v-row>
              <v-col cols="6" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Batch Code</p>
                </v-col>
                <v-text-field
                  outlined
                  dense
                  v-model="form.batch_code"
                  required
                  clearable
                  placeholder="Enter Batch Code"
                  :rules="[rules.required]"
                ></v-text-field>
              </v-col>

              <v-col cols="6" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Manufacture Date</p>
                </v-col>
                <DatePicker
                  v-model="form.manufacture_date"
                  placeholder="Manufacture Date"
                  :max="new Date().toISOString().substr(0, 10)"
                  :rules="[rules.dateValidation]"
                ></DatePicker>
              </v-col>
              <v-row align="center">
                <v-col cols="3" sm="3">
                  <v-checkbox
                    v-model="form.not_expired"
                    label="Not Expired"
                    dense
                    @change="handleNotExpired"
                    class="mt-4"
                  ></v-checkbox>
                </v-col>
                <v-col cols="8" sm="8" v-if="!form.not_expired">
                  <v-row>
                    <v-col class="pa-0 ma-0" cols="12">
                      <p class="text-title">Expiry Date</p>
                    </v-col>
                    <v-col class="pa-0 ma-0" cols="12">
                      <DatePicker
                        v-model="form.expiry_date"
                        placeholder="Expiry Date"
                      ></DatePicker>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>

              <v-col cols="6" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Product</p>
                </v-col>
                <v-autocomplete
                  outlined
                  dense
                  v-model="form.product_id"
                  required
                  clearable
                  placeholder="Select Product"
                  :items="products"
                  :item-text="productDisplay"
                  item-value="id"
                  :rules="[rules.required]"
                ></v-autocomplete>
              </v-col>

              <v-col cols="6" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Product Cost</p>
                </v-col>
                <v-text-field
                  outlined
                  dense
                  v-model="form.cost"
                  required
                  clearable
                  placeholder="Enter Product Cost"
                ></v-text-field>
              </v-col>

              <v-col cols="6" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Retail Price</p>
                </v-col>
                <v-text-field
                  outlined
                  dense
                  v-model="form.retail_price"
                  required
                  clearable
                  placeholder="Enter Retail Price"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-col cols="6" sm="6">
              <v-col class="pa-0 ma-0">
                <p class="text-title">Wholesale price</p>
              </v-col>
              <v-text-field
                outlined
                dense
                v-model="form.wholesale_price"
                required
                clearable
                placeholder="Enter Wholesale price"
              ></v-text-field>
            </v-col>

            <v-card-actions class="mt-10">
              <v-row>
                <v-col cols="12" md="10" xs="6"></v-col>
                <v-col cols="12" md="2" xs="6">
                  <v-btn
                    v-if="hasPermission('batch.create')"
                    large
                    color="#00B3FF"
                    dark
                    depressed
                    block
                    @click="submit"
                  >
                    Create Batch
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-form>

    <Snackbars
      :text="succesMsg"
      :snackbar="snackbar"
      :color="'success'"
      @classSnackbar="closeSnackbar"
    />
  </div>
</template>

<script>
import DatePicker from "@/components/shared/DatePicker";
import Alert from "@/components/shared/Alert";
import Snackbars from "@/components/shared/Snackbars";
import HeaderDividerBlock from "@/components/shared/HeaderDividerBlock";
import { BatchService } from "@/services/batch-service.js";
import { checkError } from "@/config/errors.js";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");

export default {
  name: "CreateBatchForm",
  components: {
    Alert,
    Snackbars,
    DatePicker,
    HeaderDividerBlock,
  },
  props: {
    productId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      valid: false,
      loading: false,
      errors: null,
      snackbar: false,
      succesMsg: "",
      rules: {
        required: (value) => !!value || "This field is required.",

        dateValidation: (value) => {
          if (!value) return "This field is required.";
          if (new Date(value) > new Date()) {
            return "Date cannot be in the future";
          }
          return true;
        },
      },
      form: {
        batch_code: "",
        manufacture_date: new Date().toISOString().substr(0, 10),
        expiry_date: new Date().toISOString().substr(0, 10),
        product_id: this.productId || "",
        cost: "",
        retail_price: "",
        wholesale_price: "",
        not_expired: false,
      },
      search: "",
      products: [],
    };
  },
  computed: {
    ...mapGetters({
      hasPermission: "hasPermission",
    }),
  },
  created() {
    this.fetchProducts();
  },

  watch: {
    productId(newVal) {
      this.form.product_id = newVal;
    },
  },

  methods: {
    productDisplay(item) {
      if (!item) return "";
      // Show 'name (product_code)' if both exist, fallback if missing
      if (item.name && item.product_code) {
        return `${item.name} (${item.product_code})`;
      }
      return item.product_code || item.name || "";
    },
    async submit() {
      const isValid = this.$refs.BatchForm.validate();
      if (!isValid) return;

      this.loading = true;
      this.errors = null;
      try {
        await BatchService.createBatch(this.form);
        this.$emit("batchCreated", this.form);
        this.$emit("close");

        this.succesMsg = "Batch created successfully!";
        this.snackbar = true;
        this.resetForm();
      } catch (error) {
        this.loading = false;
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.form = {
        batch_code: "",
        manufacture_date: new Date().toISOString().substr(0, 10),
        expiry_date: new Date().toISOString().substr(0, 10),
        product_id: "",
        cost: "",
        retail_price: "",
        wholesale_price: "",
      };
    },
    async fetchProducts() {
      try {
        const products = await BatchService.getProducts();
        this.products = products.data.data;
      } catch (err) {
        console.error(err);
        this.errors =
          err.response?.data || "Error fetching products or GRN list.";
      }
    },
    handleNotExpired() {
      if (this.form.not_expired) {
        this.form.expiry_date = null;
      } else {
        this.form.expiry_date = new Date().toISOString().substr(0, 10);
      }
    },
    closeSnackbar() {
      this.snackbar = false;
    },
  },
};
</script>
